import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/Icon'
import type { IconName } from '@/data/content'
import { Chip } from './core'

/* ================================================================== */
/*  Composed cards: the recurring social objects of LifeCraft4U.         */
/*  Events, communities, hosts, member voices. All dual-skin.          */
/* ================================================================== */

/* ---- DateBadge -------------------------------------------------------- */
export function DateBadge({ day, time, className }: { day: string; time: string; className?: string }) {
  return (
    <div className={cn('lc-glass-soft grid w-16 shrink-0 place-items-center rounded-tile px-2 py-2.5 text-center', className)}>
      <span className="label !text-[11px] text-lc-accent">{day}</span>
      <span className="mt-0.5 text-[15px] font-bold leading-none text-lc-ink">{time}</span>
    </div>
  )
}

/* ---- EventRow: horizontal list form ------------------------------------ */
export function EventRow({
  day,
  time,
  kicker,
  title,
  blurb,
  href = '#',
  className,
}: {
  day: string
  time: string
  kicker: string
  title: string
  blurb: string
  href?: string
  className?: string
}) {
  return (
    <a href={href} className={cn('lc-glass group flex items-center gap-3.5 rounded-card p-3.5 pr-4 transition-transform duration-300 ease-brand hover:-translate-y-0.5 sm:gap-5 sm:p-4 sm:pr-5', className)}>
      <DateBadge day={day} time={time} className="w-14 sm:w-16" />
      <span className="min-w-0 flex-1">
        <span className="label block !text-[10px] text-lc-mute sm:!text-[11px]">{kicker}</span>
        <span className="mt-0.5 block truncate font-display text-lg text-lc-ink sm:text-xl">{title}</span>
        <span className="mt-0.5 block truncate text-[13px] text-lc-mute">{blurb}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <span className="label hidden rounded-pill bg-lime px-2.5 py-1 !text-[11px] text-night opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:inline-block">save my seat</span>
        <span className="text-lc-mute transition-transform duration-300 group-hover:translate-x-1">→</span>
      </span>
    </a>
  )
}

/* ---- CommunityCard ----------------------------------------------------- */
export function CommunityCard({
  img,
  name,
  topic,
  tagline,
  to = '#',
  soon,
  focus,
  className,
}: {
  img: string
  name: string
  topic: string
  tagline?: string
  to?: string
  soon?: boolean
  focus?: string
  className?: string
}) {
  const inner = (
    <>
      <div className="relative h-40 overflow-hidden">
        <img src={img} alt="" style={focus ? { objectPosition: focus } : undefined} className={cn('h-full w-full object-cover transition-transform duration-700 ease-brand', !soon && 'group-hover:scale-105 motion-reduce:group-hover:scale-100', soon && 'photo-tint opacity-80')} />
        <Chip small tone="glass" className="absolute left-3 top-3 border-transparent !bg-night/70 !text-cream backdrop-blur">{topic}</Chip>
      </div>
      <div className="flex items-center justify-between p-4">
        <div>
          <h4 className="font-display text-xl text-lc-ink">{name}</h4>
          {tagline && <p className="mt-0.5 text-[13px] text-lc-mute">{tagline}</p>}
        </div>
        {soon ? (
          <span className="label rounded-pill border border-lc-line px-2.5 py-1 !text-[11px] text-lc-mute">Coming soon</span>
        ) : (
          <span className="text-lc-mute transition-transform duration-300 group-hover:translate-x-1">→</span>
        )}
      </div>
    </>
  )
  const cls = cn('lc-glass group block w-full max-w-[300px] overflow-hidden rounded-card', !soon && 'transition-transform duration-300 ease-brand hover:-translate-y-1', className)
  if (soon) return <div className={cls} aria-label={`${name}, opening soon`}>{inner}</div>
  return <Link to={to} className={cls}>{inner}</Link>
}

/* ---- HostCard (LifeCrafter) -------------------------------------------- */
export function HostCard({
  img,
  name,
  years,
  craft,
  className,
}: {
  img: string
  name: string
  years: string
  craft: string
  className?: string
}) {
  return (
    <figure className={cn('lc-glass group w-full max-w-[260px] overflow-hidden rounded-card', className)}>
      <div className="relative h-56 overflow-hidden">
        <img src={img} alt={name} className="h-full w-full object-cover transition-transform duration-700 ease-brand group-hover:scale-105 motion-reduce:group-hover:scale-100" />
        <span className="label absolute left-3 top-3 rounded-pill bg-night/70 px-2.5 py-1 !text-[11px] text-cream backdrop-blur">{years}</span>
      </div>
      <figcaption className="p-4">
        <p className="font-display text-xl text-lc-ink">{name}</p>
        <p className="mt-0.5 text-[13px] leading-snug text-lc-mute">{craft}</p>
        <p className="label mt-3 flex items-center gap-1.5 !text-[11px] text-lc-accent">
          <Icon name="shield" size={12} /> verified LifeCrafter
        </p>
      </figcaption>
    </figure>
  )
}

/* ---- EmptyState ---------------------------------------------------------- */
export function EmptyState({
  icon = 'compass',
  title,
  line,
  action,
  className,
}: {
  icon?: IconName
  title: string
  line: string
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('lc-glass-soft grid place-items-center rounded-card px-8 py-12 text-center', className)}>
      <span className="lc-glass grid h-14 w-14 place-items-center rounded-full text-lc-accent">
        <Icon name={icon} size={22} />
      </span>
      <h4 className="mt-5 font-display text-2xl text-lc-ink">{title}</h4>
      <p className="mt-2 max-w-xs text-[14px] leading-relaxed text-lc-mute">{line}</p>
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}
